import {
	CONSENT_COOKIE_MAX_AGE_SECONDS,
	CONSENT_COOKIE_NAME,
	CONSENT_COOKIE_VERSION,
	type UsercentricsDecision,
} from './consent-cookie';
/**
 * The CMP's v3 browser API, including the undocumented `changeLanguage` and `getActiveLanguage`.
 *
 * How this module drives the CMP, and what was verified against it: docs/notes/ui/usercentrics.md
 */
interface UsercentricsCmp {
	readonly getActiveLanguage: () => Promise<string>;
	readonly changeLanguage: (language: string) => Promise<void>;
	readonly showFirstLayer: () => Promise<void>;
	readonly showSecondLayer: () => Promise<void>;
	readonly closeCmp: () => Promise<void>;
	readonly isConsentRequired: () => Promise<boolean>;
	readonly getConsentDetails: () => Promise<UsercentricsConsentDetails>;
}

/** Keyed by template id. */
interface UsercentricsConsentDetails {
	readonly services?: Record<string, { readonly name?: string; readonly consent?: { readonly given?: boolean } }>;
}

/** Lets a gated embed appear the moment the donor accepts, without waiting for a navigation. */
const CMP_CONSENT_CHANGED_EVENT = 'UC_CONSENT_CHANGED';

declare global {
	interface Window {
		__ucCmp?: UsercentricsCmp;
		UC_UI?: Pick<UsercentricsCmp, 'showSecondLayer'>;
		/** Read once by the loader at init; setting it later does nothing. */
		UC_UI_SUPPRESS_CMP_DISPLAY?: boolean;
	}
}

let cmpLoaderRequested = false;

/** Only a banner this module withheld may be re-opened on the way out. */
let bannerWithheld = false;

/**
 * Injects the CMP loader after hydration, never through `head()` (DR-24).
 *
 * `locale` must be the page's resolved one: the CMP ignores `<html lang>` and `navigator.language` and
 * otherwise opens in Spanish for everyone. `suppressBanner` can only be decided here, before the loader
 * runs. Idempotent. Details: docs/notes/ui/usercentrics.md
 */
export const loadUsercentrics = (settingsId: string, draft: boolean, locale?: string, suppressBanner = false): void => {
	if (typeof document === 'undefined' || !settingsId || cmpLoaderRequested) return;
	cmpLoaderRequested = true;
	if (suppressBanner) {
		window.UC_UI_SUPPRESS_CMP_DISPLAY = true;
		bannerWithheld = true;
	}
	const script = document.createElement('script');
	// The loader finds its own tag by this id, so id and data attributes must precede the append.
	script.id = 'usercentrics-cmp';
	script.src = 'https://web.cmp.usercentrics.eu/ui/loader.js';
	script.async = true;
	script.dataset.settingsId = settingsId;
	// An unshipped locale is a silent no-op in the CMP, so no allowlist to drift from the Admin Interface.
	if (locale) script.dataset.language = locale;
	// Unpublished config off `pro`; omitted rather than "false" so `pro` matches the documented tag.
	if (draft) script.dataset.draft = 'true';
	document.head.appendChild(script);
};

/** A locale switch never reloads the document, and the CMP reads its language only at init. */
export const syncUsercentricsLanguage = async (locale: string): Promise<void> => {
	const cmp = window.__ucCmp;
	if (!cmp || !locale) return;
	// Runs on every locale-bearing render, and re-applying the active language re-renders the banner.
	if ((await cmp.getActiveLanguage()) === locale) return;
	await cmp.changeLanguage(locale);
};

/**
 * Keeps the first layer off pages that exist to be read, and re-opens it on the way out if still required.
 *
 * `closeCmp` records nothing; the settings layer stays reachable from the footer. See
 * docs/notes/ui/usercentrics.md
 */
export const syncUsercentricsBannerVisibility = async (suppressed: boolean): Promise<void> => {
	const cmp = window.__ucCmp;
	if (!cmp) return;
	if (suppressed) {
		bannerWithheld = true;
		await cmp.closeCmp();
		return;
	}
	if (!bannerWithheld) return;
	bannerWithheld = false;
	if (!(await cmp.isConsentRequired())) return;
	await cmp.showFirstLayer();
};

/** Fails closed: callers use it to decide whether to contact a third party, so "don't know" means "don't". */
export const hasUsercentricsServiceConsent = async (serviceName: string): Promise<boolean> => {
	const cmp = window.__ucCmp;
	if (!cmp) return false;
	const details = await cmp.getConsentDetails();
	const services = Object.values(details.services ?? {});
	return services.some((service) => service.name === serviceName && service.consent?.given === true);
};

/** Readable long before the CMP itself has booted. */
const CMP_STORAGE_KEY = 'ucData';

/** Reads both CMP shapes (bare boolean, `{ given }`), so a format change degrades to "ask", never "denied". */
const readServiceConsentFlag = (service: object): boolean | undefined => {
	if (!('consent' in service)) return undefined;
	const { consent } = service;
	if (typeof consent === 'boolean') return consent;
	if (typeof consent === 'object' && consent !== null && 'given' in consent && typeof consent.given === 'boolean') {
		return consent.given;
	}
	return undefined;
};

/**
 * Answers on the first frame, where the loaded CMP takes about a second and gated content visibly flashes.
 * `undefined` means "ask the CMP".
 */
export const readStoredUsercentricsServiceConsent = (serviceName: string): boolean | undefined => {
	const decision = readStoredUsercentricsDecision();
	if (!decision) return undefined;
	return decision.granted.includes(serviceName) ? true : decision.denied.includes(serviceName) ? false : undefined;
};

/** `undefined` is "no answer yet", which callers must tell apart from "denied everything". */
export const readStoredUsercentricsDecision = (): UsercentricsDecision | undefined => {
	if (typeof localStorage === 'undefined') return undefined;
	let raw: string | null = null;
	try {
		raw = localStorage.getItem(CMP_STORAGE_KEY);
	} catch {
		// Storage can throw outright when the browser blocks it (Safari private mode, embedded contexts).
		return undefined;
	}
	if (!raw) return undefined;

	try {
		const parsed: unknown = JSON.parse(raw);
		if (typeof parsed !== 'object' || parsed === null || !('consent' in parsed)) return undefined;
		const { consent } = parsed;
		if (typeof consent !== 'object' || consent === null || !('services' in consent)) return undefined;
		const { services } = consent;
		if (typeof services !== 'object' || services === null) return undefined;

		const granted: string[] = [];
		const denied: string[] = [];
		for (const service of Object.values(services)) {
			if (typeof service !== 'object' || service === null) continue;
			if (!('name' in service) || typeof service.name !== 'string') continue;
			const given = readServiceConsentFlag(service);
			// Mirrored into `wc-consent` for SSR, so a guessed "denied" would placeholder a consenting donor.
			if (given === true) granted.push(service.name);
			else if (given === false) denied.push(service.name);
		}
		if (granted.length === 0 && denied.length === 0) return undefined;
		return { granted, denied };
	} catch {
		return undefined;
	}
};

/** Call on load and on every consent change; no readable record clears it, so SSR sees "not decided". */
export const syncConsentCookie = (): void => {
	if (typeof document === 'undefined') return;
	const secure = location.protocol === 'https:' ? '; Secure' : '';
	const decision = readStoredUsercentricsDecision();
	if (!decision) {
		document.cookie = `${CONSENT_COOKIE_NAME}=; Max-Age=0; Path=/; SameSite=Lax${secure}`;
		return;
	}
	const value = encodeURIComponent(
		JSON.stringify({ v: CONSENT_COOKIE_VERSION, g: decision.granted, d: decision.denied }),
	);
	document.cookie = `${CONSENT_COOKIE_NAME}=${value}; Max-Age=${CONSENT_COOKIE_MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secure}`;
};

/** Exact, so the gtag.js gate fails closed; a rename stops ALL analytics (docs/notes/ui/usercentrics.md). */
export const ANALYTICS_SERVICE_NAME = 'Google Analytics 4';

/** Loose on purpose: a cleanup that stops matching never runs, which looks like compliance and is not. */
const ANALYTICS_SERVICE_PATTERN = /google analytics/i;
const ANALYTICS_COOKIE_PATTERN = /^_ga(_|$)/;

/** A delete needs the `Domain` it was set with, which is unreadable; gtag.js sets `_ga` on `worldcoo.com`. */
const expireFirstPartyCookie = (name: string): void => {
	const expiry = `${encodeURIComponent(name)}=; Max-Age=0; Path=/`;
	document.cookie = expiry;
	const labels = location.hostname.split('.');
	// A cookie cannot be set on a public suffix.
	for (let i = 0; i < labels.length - 1; i++) {
		const domain = labels.slice(i).join('.');
		document.cookie = `${expiry}; Domain=${domain}`;
		document.cookie = `${expiry}; Domain=.${domain}`;
	}
};

/**
 * Deletes `_ga` cookies unless analytics was granted: Consent Mode never removes what an earlier grant wrote.
 *
 * The CMP deletes nothing, since the config declares no trackers; recheck if that changes. Call alongside
 * {@link syncConsentCookie}. Why and what else it leaves alone: docs/notes/ui/usercentrics.md
 */
export const clearCookiesForRefusedServices = (): void => {
	if (typeof document === 'undefined') return;
	const decision = readStoredUsercentricsDecision();

	if (decision && ![...decision.granted, ...decision.denied].some((name) => ANALYTICS_SERVICE_PATTERN.test(name))) {
		// Refused cannot be told from accepted-under-a-new-name, and wiping a consenting id is a measurement hole.
		console.error(new Error('No Usercentrics service matched the analytics pattern; cookie cleanup skipped'));
		return;
	}
	if (decision?.granted.some((name) => ANALYTICS_SERVICE_PATTERN.test(name))) return;

	for (const entry of document.cookie.split('; ')) {
		const name = entry.slice(0, entry.indexOf('='));
		if (name && ANALYTICS_COOKIE_PATTERN.test(name)) expireFirstPartyCookie(name);
	}
};

export const onUsercentricsConsentChanged = (listener: () => void): (() => void) => {
	if (typeof window === 'undefined') return () => undefined;
	window.addEventListener(CMP_CONSENT_CHANGED_EVENT, listener);
	return () => window.removeEventListener(CMP_CONSENT_CHANGED_EVENT, listener);
};

const CMP_READY_TIMEOUT_MS = 15_000;
const CMP_READY_POLL_MS = 200;

/** One poll for every gated embed on a page; cleared once settled, so a later mount does not inherit a `false`. */
let cmpReadyPromise: Promise<boolean> | undefined;

const waitForCmp = (): Promise<boolean> => {
	if (window.__ucCmp) return Promise.resolve(true);
	cmpReadyPromise ??= new Promise<boolean>((resolve) => {
		let waited = 0;
		const timer = window.setInterval(() => {
			waited += CMP_READY_POLL_MS;
			if (window.__ucCmp) {
				window.clearInterval(timer);
				resolve(true);
			} else if (waited >= CMP_READY_TIMEOUT_MS) {
				window.clearInterval(timer);
				resolve(false);
			}
		}, CMP_READY_POLL_MS);
	}).then((available) => {
		cmpReadyPromise = undefined;
		return available;
	});
	return cmpReadyPromise;
};

/**
 * Calls back once the CMP exists, or with `false` after the timeout (blocked or never loaded).
 *
 * Polls because nothing announces it: `UC_CONSENT_CHANGED` never fires for the decision restored at init, and
 * no init event is verified. Always answers on a microtask. See docs/notes/ui/usercentrics.md
 */
export const onUsercentricsReady = (listener: (available: boolean) => void): (() => void) => {
	if (typeof window === 'undefined') return () => undefined;
	let active = true;
	void waitForCmp().then((available) => {
		if (active) listener(available);
	});
	return () => {
		active = false;
	};
};

const CMP_THEME_STYLE_ID = 'worldcoo-cmp-primary';

/** Not `updateTheme`: every `ThemeData` shape probed silently reset the theme to grey. */
function cmpThemeCss(primary: string, onPrimary: string): string {
	return (
		'.cmp,.modal-wrapper,.privacyNotice{' +
		`--color-main-primary:${primary};` +
		`--color-links-anchor:${primary};` +
		`--color-focus:${primary};` +
		`--color-buttons-accept-background:${primary};` +
		`--color-buttons-accept-text:${onPrimary};` +
		`--color-buttons-save-background:${primary};` +
		`--color-buttons-save-text:${onPrimary};` +
		`--color-toggle-background-active:${primary};` +
		'}'
	);
}

/** Bounded: the host may never appear (EasyPrivacy, offline, embeds), and a subtree observer runs on every change. */
const whenCmpShadowRoot = (attach: (shadow: ShadowRoot) => () => void): (() => void) => {
	let detach: (() => void) | undefined;
	const tryAttach = (): boolean => {
		const shadow = document.getElementById('usercentrics-cmp-ui')?.shadowRoot;
		if (!shadow) return false;
		detach = attach(shadow);
		return true;
	};

	if (tryAttach()) return () => detach?.();

	let giveUp: number | undefined;
	let hostObserver: MutationObserver | undefined;
	const stopWaiting = (): void => {
		if (giveUp !== undefined) window.clearTimeout(giveUp);
		hostObserver?.disconnect();
	};
	hostObserver = new MutationObserver(() => {
		if (tryAttach()) stopWaiting();
	});
	hostObserver.observe(document.body, { childList: true, subtree: true });
	giveUp = window.setTimeout(stopWaiting, CMP_READY_TIMEOUT_MS);
	return () => {
		stopWaiting();
		detach?.();
	};
};

/**
 * The caller passes the colours: they live on a nested div (`DivWithPrimaryColorVariable`), not on `:root`.
 *
 * The CMP appends stylesheets on every re-render, so an observer keeps this one last, which is what wins.
 */
export const applyUsercentricsPrimaryColor = (primary: string, onPrimary: string): (() => void) | undefined => {
	if (typeof document === 'undefined' || !primary) return;
	const css = cmpThemeCss(primary, onPrimary || '#ffffff');

	const write = (shadow: ShadowRoot): void => {
		const existing = shadow.querySelector(`#${CMP_THEME_STYLE_ID}`);
		const style = existing instanceof HTMLStyleElement ? existing : document.createElement('style');
		style.id = CMP_THEME_STYLE_ID;
		if (style.textContent !== css) style.textContent = css;
		// Only when not already last, or the observer re-triggers itself.
		const styles = [...shadow.querySelectorAll('style')];
		if (styles.at(-1) !== style) shadow.append(style);
	};

	return whenCmpShadowRoot((shadow) => {
		write(shadow);
		const shadowObserver = new MutationObserver(() => write(shadow));
		shadowObserver.observe(shadow, { childList: true });
		return () => shadowObserver.disconnect();
	});
};

/** Not the CMP docs' inline `onClick` anchor: React has no inline handlers, and `#` would push history. */
export const openUsercentricsPrivacySettings = (): void => {
	const open = window.__ucCmp
		? () => window.__ucCmp?.showSecondLayer()
		: window.UC_UI
			? () => window.UC_UI?.showSecondLayer()
			: undefined;
	if (!open) {
		// Loading or blocked: nothing to recover, but silence would make a dead control undiagnosable.
		console.error(new Error('Usercentrics privacy settings requested before the CMP was available'));
		return;
	}
	void Promise.resolve(open()).catch((error: unknown) => {
		console.error(new Error(`Usercentrics failed to open the privacy settings layer: ${String(error)}`));
	});
};
